import { stripRouteLocaleFromPathname } from "./iframe-access.js";

/**
 * A Team Moderator may only manage team members (and their join requests), edit their own profile and,
 * where the clinic has them, manage its Source QR codes.
 * Everything else (home, notes, patients, settings, subscription, ...) is redirected to the team page.
 */
export const TEAM_MODERATOR_HOME_PATH = "/user/team";
export const TEAM_MODERATOR_PROFILE_PATH = "/user/edit/information";
// Source QR codes is the only Patient Intake tab a Team Moderator sees.
export const PATIENT_INTAKE_PATH = "/user/patient-intake";
export const SOURCE_QR_CODES_PATH = `${PATIENT_INTAKE_PATH}/list/source-qr-codes`;

// Logical paths (without locale prefix) a Team Moderator may open. "/" is kept so the index route can
// still run its own redirect chain (password update, terms sign, ...) before landing on the team page.
const ALLOWED_TEAM_MODERATOR_EXACT_PATHS = ["/"];
const ALLOWED_TEAM_MODERATOR_PREFIXES = [
	TEAM_MODERATOR_HOME_PATH,
	"/user/join-requests",
	TEAM_MODERATOR_PROFILE_PATH,
	"/user/edit/password",
	"/auth",
	"/loginAs",
	"/user/sign",
	"/user/email",
	"/user/mobile",
	"/support",
	"/page",
	"/maintenance",
	"/error",
];

const withLeadingSlash = (path) => (path.startsWith("/") ? path : `/${path}`);

const withoutTrailingSlash = (path) => (path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path);

const matchesAllowedPrefix = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`);

/**
 * Where a Team Moderator should be sent for the current URL, or `null` when the URL is allowed.
 * The returned path has no locale prefix; callers add `getRouteLocale(language)`.
 * `canManageSourceQrCodes` opens the Source QR codes tab and sends the rest of Patient Intake to it.
 */
export const getTeamModeratorRedirectPathname = (
	pathname = "",
	language = "",
	{ canManageSourceQrCodes = false } = {}
) => {
	const path = withoutTrailingSlash(withLeadingSlash(stripRouteLocaleFromPathname(pathname, language)));

	if (ALLOWED_TEAM_MODERATOR_EXACT_PATHS.includes(path)) return null;
	if (ALLOWED_TEAM_MODERATOR_PREFIXES.some((prefix) => matchesAllowedPrefix(path, prefix))) return null;
	if (path === "/user/edit") return TEAM_MODERATOR_PROFILE_PATH;
	if (canManageSourceQrCodes && matchesAllowedPrefix(path, PATIENT_INTAKE_PATH)) {
		return matchesAllowedPrefix(path, SOURCE_QR_CODES_PATH) ? null : SOURCE_QR_CODES_PATH;
	}

	return TEAM_MODERATOR_HOME_PATH;
};
